import React, { useState } from 'react';
import { Check, ChevronDown, AlertTriangle } from 'lucide-react';
import { Toggle } from '../settingsPrimitives';
import McpCapabilityMatrix from './McpCapabilityMatrix';
import McpClientConfigs, { CopyBlock } from './McpClientConfigs';
import McpBridgeInstall from './McpBridgeInstall';

/**
 * Settings → MCP as four numbered steps (owner, 11.10.2026: "people that don't
 * know how to set up the MCP will try first turning on the server, which won't
 * turn on without a token"). A checklist like Stripe's onboarding or a VS Code
 * walkthrough: the first unfinished step is open, finished ones fold to a tick,
 * any step can be reopened.
 *
 *   1 access key → 2 what agents may use → 3 server on → 4 connect the agent
 *
 * Permissions come BEFORE connecting on purpose: an agent that connects with
 * nothing granted sees zero tools and its user thinks the setup is broken.
 * Step 4 turns green by itself when an agent reaches the server with the
 * current key (`status.lastAgentAt`) and warns when one tried an old key.
 */
export function anyGrant(capabilities) {
  return Object.values(capabilities || {}).some((g) => g && (g.read || g.write));
}

/** An agent tried a wrong or missing key AFTER the last good request. */
export function oldKeyInUse(status) {
  return !!status?.lastRejectedAt && (!status?.lastAgentAt || status.lastRejectedAt > status.lastAgentAt);
}

export function setupState({ settings, status }) {
  return [
    !!settings?.hasToken,
    anyGrant(settings?.capabilities),
    !!status?.running,
    // A newer refused request reopens step 4: that agent needs the current key.
    !!status?.lastAgentAt && !oldKeyInUse(status),
  ];
}

const time = (ms) => new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

export default function McpSetupSteps(props) {
  const { t, settings, status, token, busy, onGenerateToken, onServerToggle, onCapabilities } = props;
  const done = setupState(props);
  const current = done.indexOf(false);
  const [opened, setOpened] = useState({});
  // A key just created stays in view next to step 2 (it is shown only once).
  const isOpen = (i) => (i in opened ? opened[i] : i === current || (i === 0 && !!token && current === 1));
  const port = status?.port ?? settings?.port ?? 8762;
  const oldKey = oldKeyInUse(status);

  const steps = [
    {
      title: t('mcp.step1Title'),
      state: done[0] ? t('mcp.step1Done') : null,
      body: (
        <>
          <p className="mcp-step-desc">{done[0] && !token ? t('mcp.step1DescExisting') : t('mcp.step1Desc')}</p>
          {token && (
            <div className="mcp-block">
              <div className="settings-row-label">{t('mcp.tokenShownOnce')}</div>
              <CopyBlock text={token} label={t('mcp.tokenCopy')} copiedLabel={t('mcp.connectCopied')} />
            </div>
          )}
          <button type="button" className="settings-action-btn" onClick={onGenerateToken}>
            {done[0] ? t('mcp.tokenRegenerate') : t('mcp.tokenGenerate')}
          </button>
        </>
      ),
    },
    {
      title: t('mcp.step2Title'),
      state: done[1] ? t('mcp.step2Done') : null,
      body: (
        <>
          <p className="mcp-step-desc">{t('mcp.step2Desc')}</p>
          <McpCapabilityMatrix t={t} capabilities={settings?.capabilities} availableSurfaces={settings?.availableSurfaces} onChange={onCapabilities} />
        </>
      ),
    },
    {
      title: t('mcp.step3Title'),
      state: done[2] ? t('mcp.step3Done', { port }) : null,
      body: (
        <div className="mcp-step-row">
          <p className="mcp-step-desc">{done[0] ? status?.lastError || t('mcp.step3Desc') : t('mcp.step3NeedsKey')}</p>
          <Toggle value={!!status?.running} disabled={busy || !done[0]} onChange={onServerToggle} label={t('mcp.enable')} />
        </div>
      ),
    },
    {
      title: t('mcp.step4Title'),
      state: done[3] ? t('mcp.step4Connected', { time: time(status.lastAgentAt) }) : null,
      body: (
        <>
          {oldKey ? (
            <div className="mcp-step-warn" role="status">
              <AlertTriangle size={14} aria-hidden /> {t('mcp.step4OldKey', { time: time(status.lastRejectedAt) })}
            </div>
          ) : (
            <p className="mcp-step-desc">{done[3] ? t('mcp.step4DescDone') : done[2] ? t('mcp.step4Waiting') : t('mcp.step4Desc')}</p>
          )}
          {/* The helper program is for Claude Desktop only, so it sits under
              that choice of the agent picker (owner, 26.10.2026). */}
          <McpClientConfigs
            t={t}
            endpoint={props.endpoint}
            token={token}
            bridgePath={props.bridgePath}
            bridgeInstall={
              <McpBridgeInstall t={t} status={props.bridge} busy={props.bridgeBusy} error={props.bridgeError} onInstall={props.onInstallBridge} />
            }
          />
        </>
      ),
    },
  ];

  return (
    <ol className="mcp-steps" aria-label={t('mcp.setupTitle')}>
      {steps.map((s, i) => (
        <li key={s.title} className={`mcp-step${done[i] ? ' is-done' : ''}${i === current ? ' is-current' : ''}`}>
          <button type="button" className="mcp-step-head" aria-expanded={isOpen(i)} onClick={() => setOpened((o) => ({ ...o, [i]: !isOpen(i) }))}>
            <span className="mcp-step-badge" aria-hidden>{done[i] ? <Check size={13} strokeWidth={3} /> : i + 1}</span>
            <span className="mcp-step-title">{s.title}</span>
            {s.state && <span className="mcp-step-state">{s.state}</span>}
            <ChevronDown size={14} className="mcp-step-chevron" aria-hidden />
          </button>
          {/* Folded steps stay mounted (hidden): their state survives a fold. */}
          <div className="mcp-step-body" hidden={!isOpen(i)}>
            {s.body}
          </div>
        </li>
      ))}
    </ol>
  );
}
